import { DROP_POSITIONS } from "../constants";

/**
 * Moves an item from one location to another relative to a target
 * @param {Array} items - The root items array
 * @param {Object} params - Configuration object
 * @param {number} params.sourceId - ID of the item to move
 * @param {number} params.targetId - ID of the target item
 * @param {string} params.placement - "top" (before target in siblings), "bottom" (after target in siblings), "middle" (into target's children)
 * @param {string} [params.childrenKey='children'] - The key to look for nested children
 * @returns {Object} - { success: boolean, items: Array, movedItem: Object, message: string }
 */
function moveItemBySourceId(items, params = {}) {
  const {
    sourceId,
    targetId,
    placement,
    childrenKey = "children",
  } = params;
  const insertBefore = placement === DROP_POSITIONS.TOP;
  // Validation
  if (sourceId === undefined || targetId === undefined) {
    return {
      success: false,
      error: "sourceId and targetId are required parameters",
      items,
    };
  }

  // Deep clone to avoid mutating original
  const clonedItems = JSON.parse(JSON.stringify(items));

  let movedItem = null;
  let sourceParent = null;
  let sourceType = null; // 'parent' or 'child'

  // ============================================================
  // FIND AND REMOVE SOURCE
  // ============================================================

  function findAndRemoveItem(itemsArray, parentItem = null) {
    // Check items array at current level
    const itemIndex = itemsArray.findIndex((item) => item.id === sourceId);

    if (itemIndex !== -1) {
      movedItem = itemsArray.splice(itemIndex, 1)[0];
      sourceParent = parentItem;
      sourceType = parentItem ? "child" : "parent";
      return true;
    }

    // Check children in each item
    for (const item of itemsArray) {
      if (item[childrenKey]) {
        const childIndex = item[childrenKey].findIndex(
          (child) => child.id === sourceId
        );

        if (childIndex !== -1) {
          movedItem = item[childrenKey].splice(childIndex, 1)[0];
          sourceParent = item;
          sourceType = "child";
          return true;
        }

        // Search nested children recursively
        if (searchNestedChildren(item[childrenKey], item)) {
          return true;
        }
      }
    }

    return false;
  }

  function searchNestedChildren(children, parentItem) {
    const childIndex = children.findIndex((child) => child.id === sourceId);

    if (childIndex !== -1) {
      movedItem = children.splice(childIndex, 1)[0];
      sourceParent = parentItem;
      sourceType = "child";
      return true;
    }

    for (const child of children) {
      if (
        child[childrenKey] &&
        searchNestedChildren(child[childrenKey], parentItem)
      ) {
        return true;
      }
    }

    return false;
  }

  // ============================================================
  // FIND AND INSERT AT TARGET
  // ============================================================

  function reorder(array) {
    array.forEach((item, index) => {
      if (item.order !== undefined) {
        item.order = index + 1;
      }
    });
  }

  function insertAtSiblingLevel(siblings, targetIndex) {
    const insertAt = insertBefore ? targetIndex : targetIndex + 1;
    siblings.splice(insertAt, 0, movedItem);
    reorder(siblings);
  }

  function insertAsChild(targetItem) {
    if (!targetItem[childrenKey]) {
      targetItem[childrenKey] = [];
    }
    targetItem[childrenKey].push(movedItem);
    reorder(targetItem[childrenKey]);
  }

  function findAndInsertItem(itemsArray) {
    const itemIndex = itemsArray.findIndex((item) => item.id === targetId);

    if (itemIndex !== -1) {
      if (placement === DROP_POSITIONS.MIDDLE) {
        insertAsChild(itemsArray[itemIndex]);
      } else {
        insertAtSiblingLevel(itemsArray, itemIndex);
      }
      return true;
    }

    for (const item of itemsArray) {
      if (item[childrenKey]) {
        const childIndex = item[childrenKey].findIndex(
          (child) => child.id === targetId
        );

        if (childIndex !== -1) {
          if (placement === DROP_POSITIONS.MIDDLE) {
            insertAsChild(item[childrenKey][childIndex]);
          } else {
            insertAtSiblingLevel(item[childrenKey], childIndex);
          }
          return true;
        }

        if (insertIntoNestedChildren(item[childrenKey])) {
          return true;
        }
      }
    }

    return false;
  }

  function insertIntoNestedChildren(children) {
    const targetIndex = children.findIndex((child) => child.id === targetId);

    if (targetIndex !== -1) {
      if (placement === DROP_POSITIONS.MIDDLE) {
        insertAsChild(children[targetIndex]);
      } else {
        insertAtSiblingLevel(children, targetIndex);
      }
      return true;
    }

    for (const child of children) {
      if (child[childrenKey] && insertIntoNestedChildren(child[childrenKey])) {
        return true;
      }
    }

    return false;
  }

  // ============================================================
  // VALIDATION
  // ============================================================

  if (sourceId === targetId) {
    return {
      success: false,
      error: "Cannot move an item relative to itself",
      items: clonedItems,
    };
  }

  // ============================================================
  // EXECUTE MOVE
  // ============================================================

  // Step 1: Find and remove the source item
  if (!findAndRemoveItem(clonedItems)) {
    return {
      success: false,
      error: `Source item with id ${sourceId} not found`,
      items: clonedItems,
    };
  }

  // Reorder remaining items in source parent
  if (sourceParent && sourceParent[childrenKey]) {
    sourceParent[childrenKey].forEach((child, index) => {
      if (child.order !== undefined) {
        child.order = index + 1;
      }
    });
  }

  // Step 2: Find target and insert relative to it
  if (!findAndInsertItem(clonedItems)) {
    return {
      success: false,
      error: `Target item with id ${targetId} not found`,
      items: clonedItems,
    };
  }

  const itemName =
    movedItem.shortcut ||
    movedItem.name ||
    movedItem.title ||
    `item ${sourceId}`;

  return {
    success: true,
    items: clonedItems,
    movedItem,
    sourceType,
    targetId,
    placement,
    childrenKey,
    message: `Item "${itemName}" moved ${placement} target (id: ${targetId})`,
  };
}

export default moveItemBySourceId;
